import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sentry, isSentryEnabled } from "@/sentry";
import type { Question, CandidateResponse } from "@/types/database";

interface UseGroupResponsesProps {
  candidateId: string | null;
  questions: Question[];
  clientIP: string;
}

export function useGroupResponses({
  candidateId,
  questions,
  clientIP,
}: UseGroupResponsesProps) {
  const [responses, setResponses] = useState<
    Record<string, { answer?: string; selected_option?: string }>
  >({});
  const [isSavingResponses, setIsSavingResponses] = useState(false);

  // Initialize responses for all questions in the group
  useEffect(() => {
    if (!candidateId || !questions.length) return;

    const initializeResponses = async () => {
      try {
        // Fetch existing responses for these questions
        const { data: existingResponses, error } = await supabase
          .from("candidate_responses")
          .select("*")
          .eq("candidate_id", candidateId)
          .in(
            "question_id",
            questions.map((q) => q.id)
          );

        if (error) {
          console.error("Error fetching existing responses:", error);
          if (isSentryEnabled) Sentry.captureException(error);
          return;
        }

        // Create response map from existing data
        const responseMap: Record<
          string,
          { answer?: string; selected_option?: string }
        > = {};

        // Initialize empty responses for all questions
        questions.forEach((question) => {
          const existingResponse = existingResponses?.find(
            (r) => r.question_id === question.id
          );
          responseMap[question.id] = {
            answer: existingResponse?.response_text || "",
            selected_option: existingResponse?.selected_option || "",
          };
        });

        setResponses(responseMap);

        // Create placeholder responses for questions that don't have responses yet
        const questionsNeedingResponses = questions.filter(
          (question) =>
            !existingResponses?.find((r) => r.question_id === question.id)
        );

        if (questionsNeedingResponses.length > 0) {
          const placeholderResponses = questionsNeedingResponses.map(
            (question) => ({
              candidate_id: candidateId,
              question_id: question.id,
              response_text: null,
              selected_option: null,
              time_taken: 0,
              ip_address: clientIP || null,
            })
          );

          const { error: insertError } = await supabase
            .from("candidate_responses")
            .insert(placeholderResponses);

            if (insertError) {
              console.error("Error creating placeholder responses:", insertError);
              if (isSentryEnabled) Sentry.captureException(insertError);
            }
        }
      } catch (error) {
        console.error("Error initializing responses:", error);
        if (isSentryEnabled) Sentry.captureException(error);
      }
    };

    initializeResponses();
  }, [candidateId, questions, clientIP]);

  const updateResponse = (
    questionId: string,
    answer?: string,
    selectedOption?: string
  ) => {
    // Update local state only
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        answer: answer !== undefined ? answer : prev[questionId]?.answer,
        selected_option:
          selectedOption !== undefined
            ? selectedOption
            : prev[questionId]?.selected_option,
      },
    }));
  };

  const saveGroupResponses = async () => {
    if (!candidateId) return;

    const responsesToSave = Object.entries(responses).map(
      ([questionId, data]) => ({
        candidate_id: candidateId,
        question_id: questionId,
        response_text: data.answer || null,
        selected_option: data.selected_option || null,
        time_taken: 0, // This could be tracked more precisely if needed
        ip_address: clientIP || null,
      })
    );

    if (responsesToSave.length === 0) {
      
      return;
    }

    try {
      setIsSavingResponses(true);

      const { error } = await supabase
        .from("candidate_responses")
        .upsert(responsesToSave, {
          onConflict: "candidate_id,question_id",
        });

      if (error) {
        console.error("Error saving group responses:", error);
        if (isSentryEnabled) Sentry.captureException(error);
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error("Error saving group responses:", error);
      if (isSentryEnabled) Sentry.captureException(error);
      return false;
    } finally {
      setIsSavingResponses(false);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    updateResponse(questionId, answer, undefined);
  };

  const handleOptionSelect = (questionId: string, option: string) => {
    updateResponse(questionId, undefined, option);
  };

  return {
    responses,
    handleAnswerChange,
    handleOptionSelect,
    saveGroupResponses,
    isSavingResponses,
  };
}
